// src/ui/Tabs.tsx (T1): underline section tabs (D's team project tabs: Overview, Brief, Messages, Rooms...).
// With `to` on every item they are links (aria-current, one URL per tab); without, they are an ARIA tablist
// that controls panels by id (`${idBase}-panel-${id}`). Root class: hb-tabs.
import type { ComponentChildren } from "preact";
import { Link } from "../app/router";
import { cx } from "./cx";

export type TabItem = {
  id: string;
  label: ComponentChildren;
  /** A quiet count after the label. */
  count?: number;
  /** A navy dot: something new behind this tab. `dotLabel` names it for assistive tech. */
  dot?: boolean;
  dotLabel?: string;
  /** Link mode: the tab's URL. */
  to?: string;
};

export function Tabs(props: {
  label: string;
  items: readonly TabItem[];
  current: string;
  onChange?: (id: string) => void;
  /** Stick under the top bar while the page scrolls. */
  sticky?: boolean;
  idBase?: string;
  class?: string;
}) {
  const { items, current } = props;
  const links = items.every((t) => t.to !== undefined);
  const base = props.idBase ?? "tabs";
  const inner = (t: TabItem) => (
    <>
      {t.label}
      {t.count !== undefined ? <span class="n">{t.count}</span> : null}
      {t.dot ? <span class="tab-dot" role="img" aria-label={t.dotLabel ?? "New"} /> : null}
    </>
  );
  if (links) {
    return (
      <nav class={cx("hb-tabs", "tabs", props.sticky && "tabs-sticky", props.class)} aria-label={props.label}>
        {items.map((t) => (
          <Link key={t.id} to={t.to!} aria-current={t.id === current ? "page" : undefined}>
            {inner(t)}
          </Link>
        ))}
      </nav>
    );
  }
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    const i = items.findIndex((t) => t.id === current);
    const next = items[(i + (e.key === "ArrowRight" ? 1 : items.length - 1)) % items.length];
    if (!next) return;
    e.preventDefault();
    props.onChange?.(next.id);
    const list = e.currentTarget as HTMLElement;
    requestAnimationFrame(() => list.querySelector<HTMLButtonElement>(`#${CSS.escape(`${base}-tab-${next.id}`)}`)?.focus());
  };
  return (
    <div class={cx("hb-tabs", "tabs", props.sticky && "tabs-sticky", props.class)} role="tablist" aria-label={props.label} onKeyDown={onKeyDown}>
      {items.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          id={`${base}-tab-${t.id}`}
          aria-selected={t.id === current ? "true" : "false"}
          aria-controls={`${base}-panel-${t.id}`}
          tabIndex={t.id === current ? 0 : -1}
          onClick={() => props.onChange?.(t.id)}
        >
          {inner(t)}
        </button>
      ))}
    </div>
  );
}

/** The panel a button-mode tab controls. */
export function TabPanel(props: { idBase?: string; id: string; current: string; class?: string; children?: ComponentChildren }) {
  const base = props.idBase ?? "tabs";
  return (
    <div role="tabpanel" id={`${base}-panel-${props.id}`} aria-labelledby={`${base}-tab-${props.id}`} hidden={props.id !== props.current} class={props.class}>
      {props.children}
    </div>
  );
}
